// Verbindung zwischen den Travelize-Seiten und dem Aufnahme-Fenster (/recorder).
//
// Die Staff-App hat keinen vue-router: jeder Klick in der Navigation laedt die Seite neu. Eine
// Aufnahme in der Seite selbst wuerde dabei abbrechen, deshalb laeuft sie in einem eigenen kleinen
// Fenster. Die Seiten melden diesem Fenster ueber einen BroadcastChannel, wo der Mitarbeiter
// gerade ist ("context"), was er oeffnet ("view", "open"), wonach er filtert ("search"), was er
// anklickt ("click"), welche Felder er aendert ("change") und was er speichert ("action") — daraus entsteht die Zeitleiste
// neben dem Transkript.
//
// Ob das Fenster lebt, wird NICHT ueber Timer/Heartbeats festgestellt: Chrome drosselt Timer in
// verdeckten Fenstern auf bis zu 1x pro Minute. Stattdessen haelt das Fenster einen Web Lock, der
// beim Schliessen oder Absturz automatisch frei wird.
import { settings } from '@clientSpecific/utilities/settings';
import { isPortalContext } from '@utilities/session';

const CHANNEL = 'trav-recorder';
const STATE_KEY = 'trav-recorder-state';

export const RECORDER_LOCK = 'trav-recorder-window';
export const RECORDER_WINDOW = 'trav-recorder';

export const isRecorderEnabled = function () {
    return !!settings.modules.recording && !isPortalContext();
};

let channel = null;
const getChannel = function () {
    if (channel === null) {
        channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : false;
    }
    return channel;
};

export const publish = function (message) {
    const c = getChannel();
    if (c) {
        c.postMessage({ ts: new Date().toISOString(), ...message });
    }
};

// Liefert eine Funktion zum Abmelden.
export const subscribe = function (handler) {
    const c = getChannel();
    if (!c) {
        return () => {};
    }
    const listener = event => handler(event.data);
    c.addEventListener('message', listener);
    return () => c.removeEventListener('message', listener);
};


/*
 =======================================================================
 * Seiten-Seite: Kontext und Aktionen melden
 */

// Seitenname aus dem Layout (z.B. Kunde + Reise) — aussagekraeftiger als der Fenstertitel,
// steht aber erst nach dem Laden der Daten fest.
let pageName = '';

export const pageContext = function () {
    const path = window.location.pathname;
    const parts = path.split('/');
    const orderId = parts[1] === 'orders' && /^\d+$/.test(parts[2] || '') ? parseInt(parts[2], 10) : null;

    let subpage = '';
    if (orderId) {
        const item = settings.order.navigation.find(entry => entry.url === (parts[3] || ''));
        subpage = item ? item.label : (parts[3] || '');
    }

    return {
        path,
        orderId,
        subpage,
        title: pageName || document.title.replace(/\s*-?\s*Travelize\s*$/, '').trim(),
    };
};

export const announcePage = function (name = null) {
    if (name) {
        pageName = name;
    }
    publish({ type: 'context', ...pageContext() });
};

// Einmal pro Seite (Header): Kontext melden und auf Nachfrage des Aufnahme-Fensters wiederholen.
export const installRecorderBridge = function () {
    announcePage();
    return subscribe(message => {
        if (message.type === 'who') {
            announcePage();
        }
    });
};

// Was die Seite speichert ("action") und welche einzelnen Datensaetze sie oeffnet ("view"), als
// Zeitleisten-Eintrag melden. Nur Methode, URL und Status — keine Inhalte. Haengt an der
// Default-Instanz von axios und sieht damit auch die Aufrufe, die nicht ueber @api laufen.
//
// "view" = Abruf eines einzelnen Datensatzes (/api/emails/123, /api/documents/5/cloud-preview-path).
// OPTIONS zaehlt mit, weil die App es fuer echte Abrufe benutzt (z.B. die Dokument-Vorschau).
//
// "search" = Abruf einer Liste MIT Filtern (/api/hotels?name=Stefanie&stars=4): zeigt, wonach der
// Mitarbeiter sucht. Nur wenn er kurz davor selbst gesucht hat (in ein Feld getippt oder in einer
// Filterzeile etwas gewaehlt) — die vielen Abrufe, mit denen eine Seite beim Laden ihre eigenen
// Daten holt (Dokumente der Reise, Todos der Kollegen), tragen auch Filter und waeren nur Rauschen.
// Beim Tippen entsteht pro Tastendruck ein Abruf — gemeldet wird je Liste nur der letzte, sobald
// kurz Ruhe ist.
const ITEM_URL = /^\/api\/[a-z0-9_\/-]+\/\d+(\/[a-z0-9_-]+)?$/i;
const VIEW_REPEAT_MS = 60000;
const SEARCH_SETTLE_MS = 1500;
// so lange nach einer Such-Eingabe gilt ein gefilterter Abruf als deren Folge
const SEARCH_INTENT_MS = 4000;
const FILTER_AREA = '.is-filters, .filters, .table-filters';
// Parameter, die keine Filter sind: Blaettern, Sortierung, Feldauswahl
const NO_FILTER_PARAM = /^(_?page|_?itemsPerPage|perPage|pagination|_groups|_order|order|properties|bearer)(\[|$)/;

// Filter eines Abrufs als lesbare Query: aus config.params und aus der URL selbst
const filterQuery = function (config) {
    const pairs = [];
    const add = (key, value) => {
        if (value === null || value === undefined || value === '' || NO_FILTER_PARAM.test(key)) {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(item => add(`${key}[]`, item));
        } else if (typeof value === 'object') {
            Object.keys(value).forEach(sub => add(`${key}[${sub}]`, value[sub]));
        } else {
            pairs.push(`${key}=${value}`);
        }
    };

    const query = (config.url || '').split('?')[1];
    if (query) {
        new URLSearchParams(query).forEach((value, key) => add(key, value));
    }
    const params = config.params;
    if (params instanceof URLSearchParams) {
        params.forEach((value, key) => add(key, value));
    } else if (params && typeof params === 'object') {
        Object.keys(params).forEach(key => add(key, params[key]));
    }

    return pairs.join('&');
};

// Was ein POST angelegt hat: die Antwort nennt den neuen Datensatz. API Platform liefert seine
// Adresse als "@id" (/api/hotel_placeholders/46205), eigene Endpunkte oft nur ein "id".
// Ohne das stuende im Protokoll nur "POST /api/hotel_placeholders" — nicht, WELCHER entstanden ist.
export const createdReference = function (data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return '';
    }
    if (typeof data['@id'] === 'string' && data['@id'] !== '') {
        return data['@id'];
    }
    if (typeof data.id === 'number' || (typeof data.id === 'string' && data.id !== '')) {
        return `id ${data.id}`;
    }
    return '';
};

export const installRecorderTimeline = function (axios) {
    const lastViews = {};
    const pendingSearches = {};

    // Wann hat der Mitarbeiter zuletzt gesucht? Tippen in ein Textfeld (auch das Suchfeld eines
    // Multiselects, z.B. Hotelsuche) oder eine Auswahl innerhalb einer Filterzeile.
    let searchedAt = 0;
    document.addEventListener('keydown', event => {
        const tag = event.target && event.target.tagName ? event.target.tagName.toLowerCase() : '';
        if (tag === 'input' || tag === 'textarea') {
            searchedAt = Date.now();
        }
    }, true);
    ['click', 'change'].forEach(name => document.addEventListener(name, event => {
        if (event.target && event.target.closest && event.target.closest(FILTER_AREA)) {
            searchedAt = Date.now();
        }
    }, true));

    const report = function (config, status, data = null) {
        const method = (config.method || '').toLowerCase();
        const url = (config.url || '').replace(window.location.origin, '').split('?')[0];
        if (url.startsWith('/api/recordings') || url.startsWith('/api/login') || url === '/api/users/me') {
            return;
        }

        if (['post', 'put', 'patch', 'delete'].includes(method)) {
            const created = method === 'post' && status < 400 ? createdReference(data) : '';
            publish({ type: 'action', method, url: created ? `${url} → ${created}` : url, status, orderId: pageContext().orderId });
            return;
        }

        if (['get', 'options'].includes(method) && status < 400 && ITEM_URL.test(url)) {
            // dieselbe Sache mehrfach kurz hintereinander (Nachladen, Polling) nur einmal melden
            const now = Date.now();
            if (lastViews[url] && now - lastViews[url] < VIEW_REPEAT_MS) {
                return;
            }
            lastViews[url] = now;
            publish({ type: 'view', method, url, status, orderId: pageContext().orderId });
            return;
        }

        if (method === 'get' && status < 400 && url.startsWith('/api/') && Date.now() - searchedAt < SEARCH_INTENT_MS) {
            const query = filterQuery(config);
            if (!query) {
                return;
            }
            const orderId = pageContext().orderId;
            clearTimeout(pendingSearches[url]);
            pendingSearches[url] = setTimeout(() => {
                delete pendingSearches[url];
                publish({ type: 'search', method, url: `${url}?${query}`, status, orderId });
            }, SEARCH_SETTLE_MS);
        }
    };

    axios.interceptors.response.use(
        response => {
            report(response.config, response.status, response.data);
            return response;
        },
        error => {
            if (error.config && error.response) {
                report(error.config, error.response.status);
            }
            return Promise.reject(error);
        }
    );
};


// Was ausserhalb der Seite geoeffnet wird ("open"): ein Dokument in Word/Excel oder ein externer
// Link. Was danach in Word passiert, sieht der Browser nicht — aber das Protokoll weiss, WELCHES
// Dokument der Mitarbeiter gerade bearbeitet, waehrend er spricht.
export const reportOpen = function (label, url) {
    // Query abschneiden: Freigabe-Links koennen dort Zugriffs-Token tragen
    const target = String(url || '').split('?')[0];
    // ein Link, dessen Text nur seine Adresse ist, braucht kein Label
    const name = /^https?:/.test(label || '') ? '' : clip(label);
    publish({ type: 'open', label: name, url: target, orderId: pageContext().orderId });
};

// Klicks auf Links, die die Seite verlassen (anderer Server, neuer Tab, Download). Normale
// Navigation innerhalb von Travelize meldet sich schon selbst als "context".
export const installRecorderLinkTracking = function () {
    document.addEventListener('click', event => {
        const link = event.target.closest ? event.target.closest('a[href]') : null;
        if (!link) {
            return;
        }
        const href = link.href || '';
        const leaves = link.target === '_blank' || link.hasAttribute('download') || (/^https?:/.test(href) && !href.startsWith(window.location.origin));
        if (leaves && !href.startsWith('javascript:')) {
            reportOpen(textOf(link) || link.title, href);
        }
    }, true);
};

// Welche Eingabefelder der Mitarbeiter aendert ("change"). Ein grosser PUT (z.B. die ganze
// Kalkulation) sagt nicht, WAS geaendert wurde — das einzelne Feld mit altem und neuem Wert schon.
//
// Ein Lauscher auf Dokument-Ebene statt Aenderungen an jeder Maske: beim Fokussieren den Wert
// merken, beim "change" (bzw. beim Verlassen eines Rich-Text-Editors) vergleichen. Erfasst native
// Felder (input, textarea, select, Datum, Checkbox), Rich-Text (contenteditable) und die Auswahl in
// vue-multiselect. Passwort- und Datei-Felder nie.
const VALUE_LIMIT = 300;

const clip = function (value) {
    const text = String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim();
    return text.length > VALUE_LIMIT ? `${text.slice(0, VALUE_LIMIT)}…` : text;
};

// Sichtbarer Text ohne Symbol-Schrift: ein <i class="material-icons">hotel</i> ist ein Bild, kein Wort
const textOf = function (element) {
    if (!element) {
        return '';
    }
    if (!element.querySelector || !element.querySelector('.material-icons, .fa')) {
        return clip(element.innerText || element.textContent);
    }
    const copy = element.cloneNode(true);
    copy.querySelectorAll('.material-icons, .fa').forEach(icon => icon.remove());
    return clip(copy.textContent);
};

const isTracked = function (element) {
    if (!element || !element.tagName) {
        return false;
    }
    if (element.isContentEditable) {
        return true;
    }
    const tag = element.tagName.toLowerCase();
    if (!['input', 'textarea', 'select'].includes(tag)) {
        return false;
    }
    const type = (element.type || '').toLowerCase();
    if (['password', 'file', 'hidden', 'submit', 'button'].includes(type)) {
        return false;
    }
    // Suchfelder sind keine Daten: das Tippen im Multiselect, die Schnellsuche und die Filterzeile
    // einer Tabelle (deren Wirkung steht schon als "search" im Protokoll)
    if (element.classList.contains('multiselect__input') || element.classList.contains('nav-search')) {
        return false;
    }
    return !element.closest('.is-filters');
};

const valueOf = function (element) {
    if (element.isContentEditable) {
        return textOf(element);
    }
    const type = (element.type || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio') {
        return element.checked ? 'ja' : 'nein';
    }
    if (element.tagName.toLowerCase() === 'select') {
        return clip([...element.selectedOptions].map(option => option.text).join(', '));
    }
    return clip(element.value);
};

// Wie heisst das Feld? Die wenigsten haben ein Label — der Reihe nach: eigenes Label, Label der
// umgebenden .field, in Tabellen "Spaltenkopf (Zeile)", sonst Platzhalter/Titel/Name.
const describeField = function (element) {
    const own = element.labels && element.labels.length ? textOf(element.labels[0]) : '';
    if (own) {
        return own;
    }
    const aria = element.getAttribute('aria-label');
    if (aria) {
        return clip(aria);
    }

    const field = element.closest('.field, .control, tr, .info-row, .level');
    const fieldLabel = field ? field.querySelector('label, .label, th') : null;
    if (fieldLabel && textOf(fieldLabel)) {
        return textOf(fieldLabel);
    }

    const cell = element.closest('.cell, td');
    const row = cell ? cell.parentElement : null;
    if (cell && row) {
        const index = [...row.children].indexOf(cell);
        const table = row.closest('.redesign-table, table');
        const head = table ? table.querySelector('.row.is-head, thead tr') : null;
        const column = head && head.children[index] ? textOf(head.children[index]) : '';
        // erste beschriftete Zelle der Zeile als Zeilenname (z.B. der Tag oder die Leistung)
        const first = [...row.children].find(child => child !== cell && textOf(child));
        const line = first ? textOf(first).slice(0, 60) : '';
        if (column || line) {
            return [column, line ? `(${line})` : ''].filter(Boolean).join(' ');
        }
    }

    return clip(element.getAttribute('placeholder') || element.getAttribute('title') || element.name || element.id || '');
};

export const installRecorderInputTracking = function () {
    const before = new WeakMap();

    const report = function (element) {
        const from = before.has(element) ? before.get(element) : '';
        const to = valueOf(element);
        if (from === to) {
            return;
        }
        before.set(element, to);
        publish({ type: 'change', label: describeField(element), from, to, orderId: pageContext().orderId });
    };

    document.addEventListener('focusin', event => {
        if (isTracked(event.target)) {
            before.set(event.target, valueOf(event.target));
        }
    }, true);

    document.addEventListener('change', event => {
        if (isTracked(event.target) && !event.target.isContentEditable) {
            report(event.target);
        }
    }, true);

    // Rich-Text-Editoren loesen kein "change" aus
    document.addEventListener('focusout', event => {
        if (isTracked(event.target) && event.target.isContentEditable) {
            report(event.target);
        }
    }, true);

    // vue-multiselect: die Auswahl ist ein Klick auf eine Option, kein Feld-Ereignis.
    // Der bisherige Wert ist nur lesbar, solange die Liste ZU ist — ein durchsuchbares Multiselect
    // blendet ihn beim Oeffnen aus. Deshalb wird er beim Oeffnen gemerkt. Ist er nicht bekannt,
    // wird das so gemeldet: ein leerer Altwert waere eine Behauptung ("war vorher leer"), die nicht
    // stimmt.
    const selectedBefore = new WeakMap();
    const currentOf = select => select.querySelector('.multiselect__single, .multiselect__tags-wrap');

    document.addEventListener('mousedown', event => {
        // ein gewaehlter Eintrag wird ueber sein x entfernt — vue-multiselect tut das schon beim
        // mousedown, beim click ist das Element nicht mehr da
        const remove = event.target.closest ? event.target.closest('.multiselect__tag-icon') : null;
        if (remove && remove.closest('.multiselect')) {
            const removedFrom = remove.closest('.multiselect');
            publish({
                type: 'change',
                label: describeField(removedFrom),
                from: textOf(remove.closest('.multiselect__tag')),
                to: '',
                orderId: pageContext().orderId,
            });
            selectedBefore.delete(removedFrom);
            return;
        }

        const select = event.target.closest ? event.target.closest('.multiselect') : null;
        if (select && !select.classList.contains('multiselect--active')) {
            selectedBefore.set(select, textOf(currentOf(select)));
        }
    }, true);

    document.addEventListener('click', event => {
        const option = event.target.closest ? event.target.closest('.multiselect__option') : null;
        const select = option ? option.closest('.multiselect') : null;
        if (!select || option.classList.contains('multiselect__option--disabled')) {
            return;
        }
        const known = selectedBefore.has(select);
        publish({
            type: 'change',
            label: describeField(select),
            from: known ? selectedBefore.get(select) : '',
            fromUnknown: !known,
            to: textOf(option),
            orderId: pageContext().orderId,
        });
        selectedBefore.set(select, textOf(option));
    }, true);
};


// Klicks, die nur die Ansicht aendern ("click"): Reiter wechseln, ein Formular oeffnen, eine Zeile
// aufklappen, eine schon geladene E-Mail anklicken. Solche Klicks loesen keinen Abruf aus und
// fehlten sonst im Protokoll. Bewusst NICHT jeder Klick: nur Bedienelemente mit lesbarer
// Beschriftung. Was sich schon anders meldet (Navigation, Links nach draussen, Eingabefelder,
// Multiselect-Auswahl, Filterzeile), bleibt hier draussen.
const CLICK_LABEL_LIMIT = 80;
// laenger = kein Bedienelement, sondern ein ganzer Bereich, der zufaellig anklickbar ist
const CLICK_TEXT_MAX = 200;
const CLICK_REPEAT_MS = 1000;
const NOT_A_CONTROL = 'input, textarea, select, label, [contenteditable="true"], .multiselect, .flatpickr-calendar, .is-filters, .btn-recorder, .tiptap, .ProseMirror';

const isControl = function (element) {
    const tag = element.tagName.toLowerCase();
    // ein Symbol ist nie selbst das Bedienelement, sondern sitzt in einem (dessen Titel zaehlt)
    if (['i', 'svg', 'path', 'img', 'span'].includes(tag) && !textOf(element)) {
        return false;
    }
    if (element.matches('.material-icons, .fa')) {
        return false;
    }
    if (tag === 'button' || tag === 'summary' || element.getAttribute('role') === 'button') {
        return true;
    }
    if (tag === 'a') {
        return true;
    }
    if (typeof element.className === 'string' && /(^|\s|-)(button|btn)(\s|-|$)/.test(element.className)) {
        return true;
    }
    return window.getComputedStyle(element).cursor === 'pointer';
};

const clickLabel = function (element) {
    const titled = element.closest('[title]');
    const text = textOf(element);
    if (text.length > CLICK_TEXT_MAX) {
        return '';
    }
    const label = text || clip(element.getAttribute('aria-label') || (titled ? titled.getAttribute('title') : ''));
    return label.length > CLICK_LABEL_LIMIT ? `${label.slice(0, CLICK_LABEL_LIMIT)}…` : label;
};

export const installRecorderClickTracking = function () {
    let last = { label: '', at: 0 };

    document.addEventListener('click', event => {
        const origin = event.target;
        if (!origin || !origin.closest || origin.closest(NOT_A_CONTROL)) {
            return;
        }

        // vom angeklickten Element nach oben das erste Bedienelement suchen
        let control = null;
        for (let element = origin, depth = 0; element && element !== document.body && depth < 5; element = element.parentElement, depth++) {
            if (isControl(element)) {
                control = element;
                break;
            }
        }
        if (!control) {
            return;
        }

        // echte Links melden sich als Seitenwechsel ("context") bzw. als "open"
        const link = control.closest('a[href]');
        const href = link ? (link.getAttribute('href') || '') : '';
        if (link && href !== '' && !href.startsWith('#') && !href.startsWith('javascript:')) {
            return;
        }

        let label = clickLabel(control);
        if (!label) {
            return;
        }
        if (control.closest('.tabs, .assignment-tabs, .tab-addition__header, [role="tablist"]')) {
            label = `Reiter ${label}`;
        } else if (control.classList.contains('row')) {
            label = `Zeile ${label}`;
        }

        // Doppelklick nur einmal
        const now = Date.now();
        if (label === last.label && now - last.at < CLICK_REPEAT_MS) {
            return;
        }
        last = { label, at: now };
        publish({ type: 'click', label, orderId: pageContext().orderId });
    }, true);
};


/*
 =======================================================================
 * Zustand des Aufnahme-Fensters (fuer den Knopf im Header)
 */

export const writeRecorderState = function (state) {
    try {
        localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) { /* voller oder gesperrter Storage: der Knopf zeigt dann nur keinen Status */ }
    publish({ type: 'state', ...state });
};

export const clearRecorderState = function () {
    localStorage.removeItem(STATE_KEY);
    publish({ type: 'state', phase: null });
};

// null, wenn kein Aufnahme-Fenster offen ist.
export const readRecorderState = async function () {
    let state;
    try {
        state = JSON.parse(localStorage.getItem(STATE_KEY));
    } catch (e) {
        state = null;
    }
    if (!state) {
        return null;
    }
    if (navigator.locks && navigator.locks.query) {
        const locks = await navigator.locks.query();
        if (!locks.held.some(lock => lock.name === RECORDER_LOCK)) {
            return null;
        }
    }
    return state;
};

// Das Fenster oeffnen oder — wenn es schon offen ist — nur nach vorne holen. Ein zweites
// window.open mit URL wuerde das Fenster neu laden und eine laufende Aufnahme beenden, deshalb
// erst ohne URL nach dem benannten Fenster greifen.
export const openRecorderWindow = function () {
    const features = 'width=440,height=720,menubar=no,toolbar=no,location=no,status=no';
    const win = window.open('', RECORDER_WINDOW, features);
    if (!win) {
        return false;
    }
    let isNew = true;
    try {
        isNew = win.location.href === 'about:blank';
    } catch (e) {
        isNew = true;
    }
    if (isNew) {
        win.location.href = '/recorder';
    }
    win.focus();
    return true;
};


/*
 =======================================================================
 * Freischaltung
 */

// Die Aufnahme ist pro Mitarbeiter freigeschaltet (User.recordingEnabled). Der Server ist die
// Instanz. Gemerkt wird fuer die Sitzung dieses Tabs nur ein "ja": wer nicht freigeschaltet ist,
// wird bei jedem Seitenaufruf neu gefragt — so erscheint der Knopf direkt nach dem Freischalten,
// ohne dass der Mitarbeiter einen neuen Tab oeffnen muss.
const ACCESS_KEY = 'trav-recorder-access';

export const fetchRecorderAccess = async function (axios) {
    try {
        const cached = JSON.parse(sessionStorage.getItem(ACCESS_KEY));
        if (cached && cached.enabled) {
            return cached;
        }
    } catch (e) { /* kein oder kaputter Eintrag: neu fragen */ }

    try {
        const response = await axios.get('/api/recordings/access');
        if (response.data.enabled) {
            sessionStorage.setItem(ACCESS_KEY, JSON.stringify(response.data));
        } else {
            sessionStorage.removeItem(ACCESS_KEY);
        }
        return response.data;
    } catch (e) {
        return { enabled: false, admin: false };
    }
};
